import { ModalClose, useBCMSModal } from "@bcm-dev/bcms-vite";
import CustomModal, { CustomModalContent, CustomModalFooter } from "components/custom-ui/CustomModal";
import React, { useEffect } from "react";
import { SVGAsset } from "res";
import "./styles.css";

/**
 * @function CustomDrawer
 * @param {STRING} id - Sets the id of the CustomModal.
 * @param {STRING} className - Allows us to set unique classes for the CustomDrawer component.
 * @param {React.Component} children - Allows us to inject content/components inside the CustomDrawer component.
 * @example
 * <CustomDrawer id="TestComponent" className="text-component">
 *   <span>This is content inside this container.</span>
 * </CustomDrawer>
 */
export default function CustomDrawer(props) {
  const id = props.id ?? "";
  const className = props.className ?? "";
  return (
    <CustomModal id={id} className={`${className} custom-drawer`}>
      {props.children}
    </CustomModal>
  );
}

/**
 * @function CustomDrawerHeader
 * @param {React.Component} children - Allows us to inject the "title" content.
 * @param {STRING} id - Allows us to apply the id to the CustomDrawer component so we can close it with ModalClose.
 * @example
 * <CustomDrawer id="TestComponent" className="test-component">
 *  <CustomDrawerHeader id="TestComponent">
 *    <h2>Title Content</h2>
 *  </CustomDrawerHeader>
 *  <div>This is extra injected content.</div>
 * </CustomDrawer>
 */
export function CustomDrawerHeader(props) {
  const id = props.id ?? "";
  const dataPoint = props.dataPoint ? { "data-point": props.dataPoint } : {};
  const { close } = useBCMSModal();

  // "useEffect" is primarily used to set a listener
  // to close the menu from the CH21 "Back" button. In that case
  // CH21 is integrated in ABI22 which is integrated in MABI23.
  useEffect(() => {
    const closeModalEventTest = evt => {
      if(evt.data.message === "closeABI22MobileMenu") {
        close(props.id);
      }
    }
    
    window.addEventListener("message", closeModalEventTest);

    return () => {
      window.removeEventListener("message", closeModalEventTest);
    };
  });

  return (
    <div className="custom-drawer-header flex-container flex-horizontal align-center">
      <div className="flex-width">
        {props.children}
      </div>
      <div className="set-width">
        <ModalClose id={id} className="clickable">
          <SVGAsset id="CloseX" className="nav-drawer-close-icon" { ...dataPoint } />
        </ModalClose>
      </div>
    </div>
  );
}

/**
 * @function CustomDrawerContent
 * @param {React.Component} children - Allows us to inject content/components isde the CustomDrawerContent component.
 * @note - This is where all the main content goes.
 * @example
 * <CustomDrawer id="TestComponent" className="test-component">
 *  <CustomDrawerHeader id="TestHeader">
 *    <h2>This is the title</h2>
 *  </CustomDrawerHeader>
 *  <CustomDrawerContent>
 *    <span>This is the content</span>
 *  </CustomDrawerContent>
 * </CustomDrawer>
 */
export function CustomDrawerContent(props) {
  return (
    <CustomModalContent className="custom-drawer-content">
      {props.children}
    </CustomModalContent>
  );
}

/**
 * @function CustomDrawerFooter
 * @param {React.Component} children - Allows us to inject content/components inside the CustomDrawerFooter.
 * @note - The injected content is almost always a button.
 * @example 
 * <CustomDrawer id="TestComponent" className="test-component">
 *  <CustomDrawerHeader id="TestHeader">
 *    <h2>This is the title</h2>
 *  </CustomDrawerHeader>
 *  <CustomDrawerContent>
 *    <span>This is the content</span>
 *  </CustomDrawerContent>
 *  <CustomDrawerFooter>
 *    <ModalPrimaryButton text="This is a button" />
 *  </CustomDrawerFooter>
 * </CustomDrawer>
 */
export function CustomDrawerFooter(props) {
  return (
    <CustomModalFooter className="custom-drawer-footer">
      {props.children}
    </CustomModalFooter>
  );
}